// Identity attribute & role mapping (TO-377, T4): how a tenant identity
// provider's attributes map onto TODAY users, their primary org unit and a
// non-admin role. The mapping is versioned per provider; the login sync and the
// admin preview evaluate it with the same pure engine (core-utils
// `identity-mapping`).
import { Role } from '../rbac/roles.js'
import type { ScimIdentityChange } from './scim.types.js'
import type { UnknownOrgUnitPolicy } from './tenant-authentication.types.js'

/** The TODAY user fields an IdP attribute can feed. `EMAIL` is the only required one. */
export enum MappedUserField {
	EMAIL = 'EMAIL',
	DISPLAY_NAME = 'DISPLAY_NAME',
	GIVEN_NAME = 'GIVEN_NAME',
	FAMILY_NAME = 'FAMILY_NAME',
	PICTURE = 'PICTURE',
	PHONE = 'PHONE',
	LOCALE = 'LOCALE',
	EMPLOYEE_ID = 'EMPLOYEE_ID',
}

/** Field rows in display order (spec identity-attribute-mapping-ui REQ-203). */
export const MAPPED_USER_FIELDS: readonly MappedUserField[] = [
	MappedUserField.EMAIL,
	MappedUserField.DISPLAY_NAME,
	MappedUserField.GIVEN_NAME,
	MappedUserField.FAMILY_NAME,
	MappedUserField.PICTURE,
	MappedUserField.PHONE,
	MappedUserField.LOCALE,
	MappedUserField.EMPLOYEE_ID,
]

/** A transform applied in TODAY to the value Cognito carried (D1). */
export enum TransformType {
	NONE = 'NONE',
	TRIM = 'TRIM',
	LOWERCASE = 'LOWERCASE',
	SPLIT_FIRST = 'SPLIT_FIRST',
	REGEX_EXTRACT = 'REGEX_EXTRACT',
	LOOKUP = 'LOOKUP',
}

/** Which org-unit key the IdP value is matched against, within the tenant only. */
export enum OrgUnitMatchBy {
	CODE = 'CODE',
	EXTERNAL_ID = 'EXTERNAL_ID',
}

/** The only roles an IdP value may select (ADR-0038 amendment, TO-377). */
export const IDP_MAPPABLE_ROLES: readonly Role[] = [Role.ORG_USER, Role.ORG_MANAGER]

export interface MappingTransform {
	type: TransformType
	/** `SPLIT_FIRST` only: 1–10 characters. */
	separator?: string
	/** `REGEX_EXTRACT` only: 1–200 characters, no flags, no nested quantifier. */
	pattern?: string
	/** `LOOKUP` only: 1–500 entries, keys and values 1–256 characters. */
	table?: Record<string, string>
}

export interface FieldRule {
	/** The IdP-side attribute name (SAML attribute / OIDC claim). */
	idpAttribute: string
	transform: MappingTransform
}

export interface OrgUnitRule extends FieldRule {
	matchBy: OrgUnitMatchBy
}

export interface RoleRule {
	idpValue: string
	role: Role
}

export interface RoleMapping extends FieldRule {
	/** Ordered; the first rule whose `idpValue` equals a derived token wins. */
	rules: RoleRule[]
	/** Applies when no rule matches. */
	defaultRole: Role
}

/** A provider's attribute mapping (spec identity-attribute-mapping REQ-001). */
export interface AttributeMapping {
	fields: Record<MappedUserField, FieldRule | null>
	/** `null` = org-unit sync off. */
	orgUnit: OrgUnitRule | null
	/** `null` = role sync off. */
	roles: RoleMapping | null
}

/** One stored (or the implicit version-0) mapping of a provider. */
export interface AttributeMappingVersionDto {
	providerId: string
	version: number
	mapping: AttributeMapping
	createdAt: string | null
	createdBy: string | null
	changedFields: string[]
}

/** A history row (no mapping body). */
export type AttributeMappingVersionSummaryDto = Omit<AttributeMappingVersionDto, 'providerId' | 'mapping'>

/** Values keyed by IdP attribute name, as captured from a test login or pasted. */
export type SampleAttributes = Record<string, string | string[]>

export interface MappingSampleDto {
	attributes: SampleAttributes
	capturedAt: string
	capturedBy: string
	expiresAt: string
}

/** Per-field outcome of evaluating a mapping. */
export enum SyncFieldOutcome {
	APPLIED = 'APPLIED',
	UNCHANGED = 'UNCHANGED',
	SKIPPED_EMPTY = 'SKIPPED_EMPTY',
	SKIPPED_INVALID = 'SKIPPED_INVALID',
	SKIPPED_CONFLICT = 'SKIPPED_CONFLICT',
	UNMAPPED = 'UNMAPPED',
}

export enum OrgUnitOutcome {
	MATCHED = 'MATCHED',
	UNKNOWN = 'UNKNOWN',
	DEFAULT_APPLIED = 'DEFAULT_APPLIED',
	REJECT_LOGIN = 'REJECT_LOGIN',
	UNMAPPED = 'UNMAPPED',
}

export enum RoleOutcome {
	RULE = 'RULE',
	DEFAULT = 'DEFAULT',
	DISABLED = 'DISABLED',
}

/** Why a mapped value was not applied (audit `skipped[].reason`). */
export enum IdpSyncSkipReason {
	EMPTY = 'EMPTY',
	NOT_A_URL = 'NOT_A_URL',
	INVALID_EMAIL = 'INVALID_EMAIL',
	INVALID_PHONE = 'INVALID_PHONE',
	INVALID_LOCALE = 'INVALID_LOCALE',
	TOO_LONG = 'TOO_LONG',
	EMAIL_TAKEN = 'EMAIL_TAKEN',
	UNKNOWN_ORG_UNIT = 'UNKNOWN_ORG_UNIT',
	DEFAULT_ORG_UNIT_INVALID = 'DEFAULT_ORG_UNIT_INVALID',
	ROLE_NOT_MAPPABLE = 'ROLE_NOT_MAPPABLE',
	/** The org-unit placement after a SCIM write's commit failed; retried on the next update (TO-379 REQ-111). */
	ORG_UNIT_UNAVAILABLE = 'ORG_UNIT_UNAVAILABLE',
}

/** `skipped[].field` also names the org-unit and role parts. */
export enum IdpSyncPart {
	ORG_UNIT = 'ORG_UNIT',
	ROLE = 'ROLE',
}

export enum IdpSyncResult {
	APPLIED = 'APPLIED',
	LOGIN_REJECTED = 'LOGIN_REJECTED',
}

export interface MappedUnitDto {
	id: string
	code: string
	name: string
	/** Ancestor names root → unit. */
	path: string[]
}

export interface MappingPreviewFieldDto {
	field: MappedUserField
	idpAttribute: string | null
	rawValue: string | null
	value: string | null
	outcome: SyncFieldOutcome
	reason: IdpSyncSkipReason | null
}

export interface MappingPreviewDto {
	fields: MappingPreviewFieldDto[]
	orgUnit: {
		outcome: OrgUnitOutcome
		value: string | null
		unit: MappedUnitDto | null
		policy: UnknownOrgUnitPolicy
	}
	role: {
		outcome: RoleOutcome
		ruleIndex: number | null
		role: Role | null
		permissionCount: number
	}
	existingUser: {
		userId: string
		diff: { field: MappedUserField; before: string | null; after: string | null }[]
		role: { before: Role[]; after: Role[]; lastIdpRole: Role | null; applies: boolean }
		orgUnit: { beforeUnitId: string | null; afterUnitId: string | null }
	} | null
}

/** Payload of `identity.user_idp_synced` (spec identity-login-sync REQ-107, scim-user-provisioning REQ-114). */
export interface UserIdpSyncedPayload {
	tenantId: string
	userId: string | null
	/** The provider of a login sync; `null` for a SCIM write. */
	providerId: string | null
	/** The tenant's SCIM connection for a SCIM write (TO-379); absent on a login sync. */
	scimConnectionId?: string | null
	mappingVersion: number
	result: IdpSyncResult
	changes: { field: MappedUserField; before: string | null; after: string | null }[]
	roleChange: { before: Role[]; after: Role[]; lastIdpRoleBefore: Role | null; lastIdpRoleAfter: Role } | null
	orgUnitChange: { beforeUnitId: string | null; afterUnitId: string } | null
	skipped: { field: MappedUserField | IdpSyncPart; reason: IdpSyncSkipReason }[]
	/** The SCIM `userName`/`externalId` change of a SCIM write (TO-379); absent on a login sync. */
	scimIdentityChange?: ScimIdentityChange | null
}

/** What a federated login must do about the attribute mapping (identity-login-sync REQ-101/REQ-106). */
export enum IdpLoginSyncDecision {
	/** No saved mapping for the provider, another tenant's provider, or not a tenant provider: no sync. */
	NO_SYNC = 'NO_SYNC',
	/** Apply the provider's current mapping after the principal is resolved or created. */
	SYNC = 'SYNC',
	/** The org-unit value is unknown and the tenant's policy is `REJECT_LOGIN`: refuse before any write. */
	REJECT_LOGIN = 'REJECT_LOGIN',
}
